import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Alert, CircularProgress, Snackbar, Typography, Divider,
  Radio, RadioGroup, FormControlLabel, FormControl, FormLabel,
} from '@mui/material';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config';

// ============================================================
// PalletEditModal — Phase 2A.2 (v2 — matches Add Entry field model)
// ------------------------------------------------------------
// Lightweight modal for pallet-level edits, using the same weight
// model as AddLineEntryModal:
//   - User enters KG only; LB auto-derives via 1 KG = 2.20 LB
//   - WT/CARTON, LOOSE per-bag are inputs
//   - TOTAL CARTON, TOT.LOOSE, total_kgs, total_pounds are computed
//   - CS radio selects CS1 or CS2; only the selected bin is editable
//
// Line-level fields (status, product, grade, pack style/size, lot,
// day code, trace id, etc.) are NOT editable here — those go through
// EditEntryModal at the line level.
//
// Uses the patched inventory.php PUT which honors explicit
// total_kgs / total_pounds in the request body.
// ============================================================

const KG_TO_LB = 2.20;

// Parse pack_size like "10/2 LB" — used to seed wt_per_carton_kg when
// the existing row has no implicit per-carton value.
const parsePackSize = (packSize) => {
  if (!packSize) return null;
  const m = String(packSize).match(/(\d+\.?\d*)\s*[Xx/]\s*(\d+\.?\d*)\s*(OZ|LB|LBS|KG|KGS)?/i);
  if (!m) return null;
  return {
    bagsPerCarton: parseFloat(m[1]) || 0,
    weightPerBag: parseFloat(m[2]) || 0,
    unit: (m[3] || 'LB').toUpperCase(),
  };
};
const perCartonKgFromPackSize = (packSize) => {
  const p = parsePackSize(packSize);
  if (!p) return 0;
  const totalInUnit = p.bagsPerCarton * p.weightPerBag;
  let lb;
  if (p.unit.includes('OZ')) lb = totalInUnit / 16;
  else if (p.unit.includes('KG')) lb = totalInUnit * KG_TO_LB;
  else lb = totalInUnit;
  return lb / KG_TO_LB;
};

const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true,
  };
};

export default function PalletEditModal({ open, onClose, onSuccess, entryData }) {
  const [formData, setFormData] = useState({
    pallet_id: '',
    cartons: 0,
    wt_per_carton_kg: '',
    loose_bags: 0,
    loose_per_bag_kg: '',
    cs_selection: 'CS1',
    cs1: '',
    cs2: '',
  });
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  // Seed form from entryData. Reverse-engineer per-carton KG from total_kgs
  // (or pack_size as fallback), and per-bag loose KG from loose_kgs / loose_bags.
  useEffect(() => {
    if (!entryData) return;

    const cartons = parseInt(entryData.cartons, 10) || 0;
    const looseBags = parseInt(entryData.loose_bags, 10) || 0;
    const looseTotalKg = parseFloat(entryData.loose_kgs) || 0;
    const totalKgs = parseFloat(entryData.total_kgs) || 0;

    // Seed wt_per_carton_kg:
    //   1) prefer back-derive from total_kgs if cartons > 0: (total - loose) / cartons
    //   2) else fall back to parsePackSize() of pack_size
    //   3) else leave empty
    let wtPerCartonKg = '';
    if (cartons > 0 && totalKgs > 0) {
      const cartonOnly = totalKgs - looseTotalKg;
      if (cartonOnly > 0) wtPerCartonKg = (cartonOnly / cartons).toFixed(2);
    }
    if (!wtPerCartonKg) {
      const fromPackSize = perCartonKgFromPackSize(entryData.pack_size);
      if (fromPackSize > 0) wtPerCartonKg = fromPackSize.toFixed(2);
    }

    // Seed loose_per_bag_kg from loose_kgs / loose_bags
    let loosePerBagKg = '';
    if (looseBags > 0 && looseTotalKg > 0) {
      loosePerBagKg = (looseTotalKg / looseBags).toFixed(2);
    }

    // Auto-detect CS selection from existing data
    const cs1Val = String(entryData.cs1 || '').trim();
    const cs2Val = String(entryData.cs2 || '').trim();
    const csSelection = cs2Val && !cs1Val ? 'CS2' : 'CS1';

    setFormData({
      pallet_id: entryData.pallet_id || '',
      cartons,
      wt_per_carton_kg: wtPerCartonKg,
      loose_bags: looseBags,
      loose_per_bag_kg: loosePerBagKg,
      cs_selection: csSelection,
      cs1: cs1Val,
      cs2: cs2Val,
    });
    setReason('');
    setError('');
  }, [entryData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // ---- derived (computed) read-only displays ----
  const derived = useMemo(() => {
    const cartons = parseInt(formData.cartons, 10) || 0;
    const looseBags = parseInt(formData.loose_bags, 10) || 0;
    const wtCKg = parseFloat(formData.wt_per_carton_kg) || 0;
    const lpKg = parseFloat(formData.loose_per_bag_kg) || 0;
    const wtCLb = wtCKg * KG_TO_LB;
    const lpLb = lpKg * KG_TO_LB;
    const totalCartonKg = cartons * wtCKg;
    const totalCartonLb = totalCartonKg * KG_TO_LB;
    const totalLooseKg = looseBags * lpKg;
    const totalLooseLb = totalLooseKg * KG_TO_LB;
    return {
      wtCLb, lpLb,
      totalCartonKg, totalCartonLb,
      totalLooseKg, totalLooseLb,
      grandKg: totalCartonKg + totalLooseKg,
      grandLb: totalCartonLb + totalLooseLb,
    };
  }, [formData]);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for this edit');
      return;
    }
    if (!String(formData.pallet_id || '').trim()) {
      setError('Pallet ID is required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const cartons = parseInt(formData.cartons, 10) || 0;
      const looseBags = parseInt(formData.loose_bags, 10) || 0;
      const total_kgs = +derived.grandKg.toFixed(2);
      const total_pounds = +derived.grandLb.toFixed(2);
      const totalLooseKg = +derived.totalLooseKg.toFixed(2);

      // ⚠️ inventory.php PUT does a full-row UPDATE — every column gets
      // overwritten. Spread original entryData first, then overlay only
      // the pallet-level fields the user is allowed to edit here.
      const res = await axios.put(
        API_ENDPOINTS.INVENTORY,
        {
          ...entryData,
          // pallet-only edits override
          pallet_id: String(formData.pallet_id).trim(),
          cartons,
          loose_bags: looseBags,
          loose_kgs: totalLooseKg, // DB stores TOTAL loose weight in this column
          total_kgs,
          total_pounds,
          // Only the selected CS column carries a value, the other is cleared
          cs1: formData.cs_selection === 'CS1' ? String(formData.cs1 || '').trim() : '',
          cs2: formData.cs_selection === 'CS2' ? String(formData.cs2 || '').trim() : '',
          // metadata
          id: entryData.id,
          reason,
        },
        getAuthConfig()
      );
      if (res.data.success) {
        setShowSuccessSnackbar(true);
        setTimeout(() => {
          onClose();
          if (onSuccess) onSuccess();
        }, 1500);
      } else {
        setError(res.data.error || 'Failed to update pallet');
      }
    } catch (err) {
      console.error('Pallet update failed:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update pallet');
    } finally {
      setLoading(false);
    }
  };

  const csKey = formData.cs_selection === 'CS1' ? 'cs1' : 'cs2';

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          background: 'linear-gradient(90deg, #C0392B 0%, #8E44AD 50%, #1F618D 100%)',
          color: 'white', fontWeight: 700, py: 1.5,
        }}>
          📦 Edit Pallet
          {entryData?.pallet_id ? ` — ${entryData.pallet_id}` : ''}
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
            <Typography variant="caption">
              Pallet-level edit. Line-level fields (status, product, grade, brand,
              pack style/size, lot no, day code, trace id) are not editable here —
              use <strong>Edit Line</strong> for those.
            </Typography>
          </Alert>

          {/* Read-only context — what line this pallet belongs to */}
          <Box sx={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25,
            bgcolor: '#f4f5f7', border: '1px solid #e0e3e8', borderRadius: 1,
            p: 1.5, mb: 2,
          }}>
            <ContextField label="STATUS" value={entryData?.status} />
            <ContextField label="PRODUCT" value={entryData?.product} />
            <ContextField label="GRADE" value={entryData?.grade} />
            <ContextField label="PACK" value={entryData?.pack_size} />
            <ContextField label="BRAND" value={entryData?.brand} />
            <ContextField label="LOT" value={entryData?.lot_no} />
          </Box>

          {/* CS selection radio + Pallet ID + selected CS bin */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap',
            p: 1.5, mb: 2, bgcolor: '#fafbfc',
            border: '1px solid #e0e3e8', borderRadius: 1,
          }}>
            <FormControl>
              <FormLabel sx={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 0.4, color: '#8E44AD !important',
              }}>
                Cold Store *
              </FormLabel>
              <RadioGroup
                row
                value={formData.cs_selection}
                onChange={(e) => handleChange('cs_selection', e.target.value)}
              >
                <FormControlLabel value="CS1"
                  control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#8E44AD' } }} />}
                  label="CS1" sx={{ mr: 2 }} />
                <FormControlLabel value="CS2"
                  control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#8E44AD' } }} />}
                  label="CS2" />
              </RadioGroup>
            </FormControl>

            <TextField
              size="small"
              label="Pallet ID *" required
              value={formData.pallet_id || ''}
              onChange={(e) => handleChange('pallet_id', e.target.value)}
              error={!String(formData.pallet_id || '').trim()}
              sx={{ minWidth: 180 }}
            />

            <TextField
              size="small"
              label={`${formData.cs_selection} (Bin)`}
              value={formData[csKey] || ''}
              onChange={(e) => handleChange(csKey, e.target.value)}
              placeholder={formData.cs_selection === 'CS1' ? 'e.g. 3A1-2' : 'e.g. 5B3-1'}
              sx={{ minWidth: 160 }}
            />
          </Box>

          <Divider sx={{ my: 2 }}>
            <Typography variant="overline" sx={{ color: '#8E44AD', fontWeight: 700, letterSpacing: 0.6 }}>
              Cartons
            </Typography>
          </Divider>

          {/* Cartons section: input + KG / derived LB / totals */}
          <Box sx={{
            display: 'grid', gap: 1.5, mb: 2,
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' },
          }}>
            <TextField
              size="small" type="number"
              label="No of Cartons *"
              value={formData.cartons ?? 0}
              onChange={(e) => handleChange('cartons', e.target.value)}
              inputProps={{ min: 0, style: { textAlign: 'right' } }}
            />
            <TextField
              size="small" type="number"
              label="WT/Carton (KG)"
              value={formData.wt_per_carton_kg || ''}
              onChange={(e) => handleChange('wt_per_carton_kg', e.target.value)}
              inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
              helperText="Override per pallet"
              FormHelperTextProps={{ sx: { fontSize: 10, m: 0 } }}
            />
            <DerivedField label="WT/Carton (LB)" value={derived.wtCLb} />
            <CalcField    label="Total Carton (KG)" value={derived.totalCartonKg} />
            <CalcField    label="Total Carton (LB)" value={derived.totalCartonLb} />
          </Box>

          <Divider sx={{ my: 2 }}>
            <Typography variant="overline" sx={{ color: '#8E44AD', fontWeight: 700, letterSpacing: 0.6 }}>
              Loose Bags
            </Typography>
          </Divider>

          {/* Loose section: input + KG per bag / derived LB / totals */}
          <Box sx={{
            display: 'grid', gap: 1.5, mb: 2,
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' },
          }}>
            <TextField
              size="small" type="number"
              label="Loose Bags"
              value={formData.loose_bags ?? 0}
              onChange={(e) => handleChange('loose_bags', e.target.value)}
              inputProps={{ min: 0, style: { textAlign: 'right' } }}
            />
            <TextField
              size="small" type="number"
              label="Loose KGs (per bag)"
              value={formData.loose_per_bag_kg || ''}
              onChange={(e) => handleChange('loose_per_bag_kg', e.target.value)}
              inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
              helperText="Per single loose bag"
              FormHelperTextProps={{ sx: { fontSize: 10, m: 0 } }}
            />
            <DerivedField label="Loose LB (per bag)" value={derived.lpLb} />
            <CalcField    label="Tot.Loose (KG)" value={derived.totalLooseKg} />
            <CalcField    label="Tot.Loose (LB)" value={derived.totalLooseLb} />
          </Box>

          {/* Grand total banner */}
          <Box sx={{
            mt: 1, mb: 2, p: 1.5, bgcolor: '#FEF5E7', borderRadius: 1,
            border: '1px solid #f5d99b',
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
          }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#8E44AD' }}>
              📊 Pallet Total
            </Typography>
            {(derived.grandKg > 0) ? (
              <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, Consolas, monospace', color: '#1F618D', fontWeight: 700 }}>
                {derived.grandKg.toFixed(2)} kg / {derived.grandLb.toFixed(2)} lb
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Fill in cartons / WT to see total
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              Stored to DB exactly as shown
            </Typography>
          </Box>

          {/* Reason — required */}
          <TextField
            fullWidth size="small" multiline rows={2}
            required
            label="Reason for Edit *"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={!reason.trim()}
            helperText="Required — explain why this pallet is being changed"
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff3e0' } }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !reason.trim() || !String(formData.pallet_id || '').trim()}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{
              background: 'linear-gradient(90deg, #C0392B 0%, #8E44AD 50%, #1F618D 100%)',
              fontWeight: 600,
              '&:hover': { opacity: 0.92 },
              '&.Mui-disabled': { opacity: 0.5, color: 'white' },
            }}
          >
            {loading ? 'Saving…' : 'Save Pallet'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ fontWeight: 600 }}>
          ✅ Pallet updated successfully
        </Alert>
      </Snackbar>
    </>
  );
}

// ---- small read-only field display helpers ----
function ContextField({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{value || '—'}</Typography>
    </Box>
  );
}

// Per-unit derived field (e.g. WT/Carton LB) — light tint, gray text, monospaced
function DerivedField({ label, value }) {
  return (
    <Box sx={{
      bgcolor: '#fbf7fc', border: '1px solid #e6d6f0', borderRadius: 1,
      p: '6px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center',
      minHeight: 40,
    }}>
      <Typography variant="caption" sx={{ fontSize: 10, color: '#6b7280', fontWeight: 600, lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography sx={{
        fontFamily: 'ui-monospace, Consolas, monospace',
        color: '#6b7280', fontSize: 13, fontWeight: 500, textAlign: 'right',
      }}>
        {value > 0 ? value.toFixed(2) : '—'}
      </Typography>
    </Box>
  );
}

// Pallet-total computed field (e.g. Total Carton KG) — bolder, brand blue
function CalcField({ label, value }) {
  return (
    <Box sx={{
      bgcolor: '#f5edf7', border: '1px solid #d6c1e0', borderRadius: 1,
      p: '6px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center',
      minHeight: 40,
    }}>
      <Typography variant="caption" sx={{ fontSize: 10, color: '#8E44AD', fontWeight: 700, lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography sx={{
        fontFamily: 'ui-monospace, Consolas, monospace',
        color: '#1F618D', fontSize: 13, fontWeight: 700, textAlign: 'right',
      }}>
        {value > 0 ? value.toFixed(2) : '—'}
      </Typography>
    </Box>
  );
}
